import React, {useCallback, useEffect, useState} from "react";

interface AuthContextType {
    token: string | null | undefined
    institution: string | null | undefined
    hasMultiInst: boolean | undefined
    isLoggedIn: boolean
    login: (token: string, expirationTime: string, institutions: number[]) => void
    logout: () => void
    refresh: (token: string, expirationTime: string) => void
    changeInstitution: (newInstitution: number) => void
    sessionTime: number
}

interface TokenObject {
    token: string | null
    institution: string | null
    hasMultiInst: boolean
    duration: number
}

const AuthContext = React.createContext<AuthContextType>({
    token: null,
    institution: null,
    hasMultiInst: false,
    isLoggedIn: false,
    login: (token, expirationTime, institutions) => {},
    logout: () => {},
    refresh: (token, expirationTime) => {},
    changeInstitution: (newInstitution: number) => {},
    sessionTime: 0
})

const calculateRemainingTime = (expirationTimeStored: string) => {
    const currentTime = new Date().getTime();
    const expirationTime = new Date(expirationTimeStored).getTime();
    return expirationTime - currentTime;
}

const retrieveStoredToken = ():TokenObject | null => {
    const storedToken = localStorage.getItem('token');
    const storedInstitution = localStorage.getItem('institution')
    const storedExpirationDate = localStorage.getItem('expirationTime');
    const hasMultiInst = localStorage.getItem('hasMultiInst') === 'true';
    if(storedExpirationDate !== null){
        const remainingTime = calculateRemainingTime(storedExpirationDate);

        //1min before it expires
        if(remainingTime <= 60000){
            localStorage.clear();
            return null;
        }

        return {
            token: storedToken,
            institution: storedInstitution,
            hasMultiInst: hasMultiInst,
            duration: remainingTime
        }
    }else{
        return null;
    }

}

export const AuthContextProvider:React.FC<{children: any}> = (props) => {
    let tokenData = retrieveStoredToken();
    let initialToken, initialInstitution, initialHasMultiInst;

    if(tokenData) {
        initialToken = tokenData.token;
        initialInstitution = tokenData.institution;
        initialHasMultiInst = tokenData.hasMultiInst
    }

    const [token, setToken] = useState(initialToken);
    const [institution, setInstitution] = useState(initialInstitution);
    const [hasMultiInst, setHasMultiInst] = useState(initialHasMultiInst);

    const userIsLoggedIn = !!token;

    const logoutHandler = useCallback(() => {
        setToken('');
        localStorage.clear();
    }, []);

    const loginHandler = (token: string, expirationTime: string, institutions: number[]) => {
        setToken(token);
        setInstitution(institutions[0].toString());
        setHasMultiInst(institutions.length > 1);
        localStorage.setItem('token', token);
        localStorage.setItem('expirationTime', expirationTime);
        localStorage.setItem('institution', institutions[0].toString());
        localStorage.setItem('hasMultiInst', String(institutions.length > 1));
    }

    const refreshHandler = (token: string, expirationTime: string) => {
        setToken(token);
        localStorage.setItem('token', token);
        localStorage.setItem('expirationTime', expirationTime);
        if(tokenData) {
            tokenData = {...tokenData, token: token, duration: calculateRemainingTime(expirationTime)};
        }
    }

    const changeInstitutionHandler = (newInstitution: number) => {
        setInstitution(newInstitution.toString());
        localStorage.setItem('institution', newInstitution.toString());
    }

    const contextValue = {
        token: token,
        institution: institution,
        hasMultiInst: hasMultiInst,
        isLoggedIn: userIsLoggedIn,
        login: loginHandler,
        logout: logoutHandler,
        refresh: refreshHandler,
        changeInstitution: changeInstitutionHandler,
        sessionTime: tokenData ? tokenData.duration : 0
    }

    return <AuthContext.Provider value={contextValue}>
        {props.children}
    </AuthContext.Provider>
}

export default AuthContext;


